/**
 * ChipTagInput.tsx
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Multi-value input with chip display for arrays (e.g., excluded_tables).
 */

import React, { useState, useCallback, KeyboardEvent } from 'react';
import { Info, X } from 'lucide-react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { AppTooltip } from '@/components/ui';

export interface ChipTagInputProps<T extends FieldValues> {
  /** Field name for form control */
  name: Path<T>;
  /** Form control from react-hook-form */
  control: Control<T>;
  /** Field label */
  label: string;
  /** Whether field is required */
  required?: boolean;
  /** Optional tooltip text */
  tooltip?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Maximum number of items allowed */
  maxItems?: number;
  /** Predefined suggestions */
  suggestions?: string[];
  /** Whether to allow free input (not just suggestions) */
  freeSolo?: boolean;
  /** Whether field is disabled */
  disabled?: boolean;
}

/**
 * ChipTagInput - Multi-value chip input with autocomplete support
 */
export function ChipTagInput<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  tooltip,
  placeholder = 'Type and press Enter',
  maxItems = 100,
  suggestions = [],
  freeSolo = true,
  disabled = false,
}: ChipTagInputProps<T>): React.ReactElement {
  const [inputValue, setInputValue] = useState('');

  const handleDelete = useCallback(
    (
      valueToDelete: string,
      currentValue: string[],
      onChange: (value: unknown) => void
    ) => {
      const newValue = currentValue.filter((v) => v !== valueToDelete);
      onChange(newValue);
    },
    []
  );

  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center">
        <span className="text-xs font-medium text-[var(--color-text-secondary)]">
          {label}
          {required && (
            <span className="ml-0.5 text-[var(--color-status-danger)]">*</span>
          )}
        </span>
        {tooltip && (
          <AppTooltip title={tooltip} placement="top">
            <Info className="ml-1 h-3.5 w-3.5 cursor-help text-[var(--color-text-secondary)]" />
          </AppTooltip>
        )}
      </div>

      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? `${label} is required` : false,
          validate: (value: string[]) => {
            if (value && value.length > maxItems) {
              return `Maximum ${maxItems} items allowed`;
            }
            return true;
          },
        }}
        render={({ field: { value = [], onChange }, fieldState: { error } }) => {
          const currentValue: string[] = Array.isArray(value) ? value : [];
          const isAtLimit = currentValue.length >= maxItems;
          const isDisabled = disabled || isAtLimit;

          const addValue = (raw: string) => {
            const trimmed = raw.trim();
            if (!trimmed) return;
            if (currentValue.includes(trimmed)) return;
            if (currentValue.length >= maxItems) return;
            if (!freeSolo && !suggestions.includes(trimmed)) return;
            onChange([...currentValue, trimmed]);
            setInputValue('');
          };

          const filteredSuggestions = suggestions.filter(
            (s) =>
              !currentValue.includes(s) &&
              inputValue.trim() !== '' &&
              s.toLowerCase().includes(inputValue.trim().toLowerCase())
          );

          const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addValue(inputValue);
            } else if (
              e.key === 'Backspace' &&
              inputValue === '' &&
              currentValue.length > 0
            ) {
              e.preventDefault();
              onChange(currentValue.slice(0, -1));
            }
          };

          return (
            <>
              <div className="relative">
                <div
                  className={[
                    'flex flex-wrap items-center gap-1 rounded-[var(--radius-md)] border bg-[var(--color-bg-card)] px-2 py-1.5',
                    'focus-within:ring-2 focus-within:ring-[var(--color-accent)]',
                    error
                      ? 'border-[var(--color-status-danger)]'
                      : 'border-[var(--color-border)]',
                    isDisabled ? 'opacity-50' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {currentValue.map((option) => (
                    <span
                      key={option}
                      className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] bg-[var(--color-bg-subtle)] px-2 py-0.5 text-xs text-[var(--color-text-primary)]"
                    >
                      {option}
                      {!disabled && (
                        <button
                          type="button"
                          aria-label={`Remove ${option}`}
                          onClick={() =>
                            handleDelete(option, currentValue, onChange)
                          }
                          className="inline-flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  <input
                    role="combobox"
                    aria-expanded={filteredSuggestions.length > 0}
                    aria-controls={`${String(name)}-listbox`}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isAtLimit ? 'Maximum reached' : placeholder}
                    disabled={isDisabled}
                    className="min-w-[120px] flex-1 bg-transparent text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:outline-none disabled:cursor-not-allowed"
                  />
                </div>

                {filteredSuggestions.length > 0 && !isDisabled && (
                  <ul
                    id={`${String(name)}-listbox`}
                    role="listbox"
                    className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1 shadow-[var(--shadow-md)]"
                  >
                    {filteredSuggestions.map((s) => (
                      <li
                        key={s}
                        role="option"
                        aria-selected={false}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addValue(s);
                        }}
                        className="cursor-pointer px-3 py-1.5 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {error && (
                <p className="mt-1 text-xs text-[var(--color-status-danger)]">
                  {error.message}
                </p>
              )}
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                {currentValue.length} / {maxItems} items
              </p>
            </>
          );
        }}
      />
    </div>
  );
}

export default ChipTagInput;
